import {
  Button,
  Card,
  CardBody,
  HStack,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/solid';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  IdentityType,
  IdentityVerificationStatus,
  UboRole,
  useGetCompanyQuery,
} from '@blockpulse3/graphql/hooks';
import {
  ErrorQueryCard,
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { canUserUpdateCompany } from './utils';

type Props = {
  onOpen: () => void;
};

export function SettingsCompanyOwners({ onOpen }: Props): JSX.Element {
  const i18nWorkspaceSettings = useTranslations('WorkspaceSettings');
  const i18nCompanyOwners = useTranslations('CompanyOwners');

  const { user } = useUser();
  const { companyId = '' } = useParams();
  const { data, loading, error } = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });

  if (error || !data) {
    return (
      <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: 5, lg: 8 }}>
        <Text flexBasis={{ lg: 250 }} fontSize="lg" fontWeight="medium">
          {i18nWorkspaceSettings('OwnersTitle')}
        </Text>
        <ErrorQueryCard h="100px" maxW={{ lg: '3xl' }} w="full" />
      </Stack>
    );
  }

  const company = data?.company;
  const ubos = company?.ubos || [];
  const isEditDisabled =
    !canUserUpdateCompany(user, data) ||
    data.company.kybVerificationStatus === IdentityVerificationStatus.APPROVED;

  return (
    <Stack
      alignItems="flex-start"
      direction={{ base: 'column', lg: 'row' }}
      spacing={{ base: 5, lg: 8 }}
    >
      <HStack alignItems="center" flexBasis={{ lg: 250 }}>
        <Text fontSize="lg" fontWeight="medium">
          {i18nWorkspaceSettings('OwnersTitle')}
        </Text>
      </HStack>
      <Skeleton isLoaded={!loading} maxWidth={{ lg: '3xl' }} width="full">
        <Card maxW={{ lg: '3xl' }} variant="divider-bottom">
          <CardBody>
            <Stack spacing="4">
              {ubos.length === 0 && (
                <Text color="gray.500">{i18nWorkspaceSettings('NoOwners')}</Text>
              )}
              {ubos.map((ubo) => {
                const individual = ubo?.individualIdentity;
                const name = individual
                  ? `${individual.firstName} ${individual.lastName}`
                  : `${ubo.firstName} ${ubo.lastName}`;

                return (
                  <Stack
                    key={ubo.id}
                    alignItems="flex-start"
                    direction={['column', 'row']}
                    spacing="2"
                  >
                    <Heading flexBasis="150px" size="sm">
                      {ubo.role === UboRole.HasCapital ? (
                        <>
                          {ubo.sharePercentage}%
                          <Text fontSize="xs">
                            {i18nCompanyOwners(ubo.isDirect ? 'Direct' : 'Indirect')}
                          </Text>
                        </>
                      ) : (
                        i18nCompanyOwners(ubo.role as keyof IntlMessages['CompanyOwners'])
                      )}
                    </Heading>
                    <IdentityCard>
                      <IdentityAvatar
                        boxSize="9"
                        src={individual?.identity?.profilePicture}
                        type={IdentityType.INDIVIDUAL}
                      />
                      <Stack spacing="0">
                        <IdentityCardTitle
                          fontSize="md"
                          isChecked={
                            individual?.kycVerificationStatus ===
                            IdentityVerificationStatus.APPROVED
                          }
                        >
                          <Text>{name}</Text>
                        </IdentityCardTitle>
                        <IdentityCardDescription>
                          {individual ? (
                            <>
                              <Text>{individual?.address?.formatted}</Text>
                              <Text>{individual.email}</Text>
                              <Text>{individual.phone}</Text>
                            </>
                          ) : (
                            <Text>{ubo?.address?.formatted}</Text>
                          )}
                        </IdentityCardDescription>
                      </Stack>
                    </IdentityCard>
                  </Stack>
                );
              })}
              <Button isDisabled={isEditDisabled} variant="secondary" onClick={onOpen}>
                <Icon as={PencilIcon} mr="1" /> {i18nWorkspaceSettings('Edit')}
              </Button>
            </Stack>
          </CardBody>
        </Card>
      </Skeleton>
    </Stack>
  );
}

export type SettingsCompanyOwnersProps = Props;
